import React from 'react';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, Table, Tbody,
  Td, Text, Th, Thead, Tr,
} from '@chakra-ui/react';
import { countWord, shortTenantId } from '../../utils/tenantAccess';
import StatusAlert from '../ElementaryComponents/StatusAlert';
import { NAVY, Panel, navyPress } from './TenantAccessLayout';

const changeBadge = (changeType) => {
  if (changeType === 'New') {
    return <Badge colorScheme="green">New</Badge>;
  }
  if (changeType === 'Role update') {
    return <Badge colorScheme="blue">Role update</Badge>;
  }
  return <Badge>No change</Badge>;
};

const countCards = (entries) => (
  <Flex gap={3} wrap="wrap">
    {entries.map(([label, value]) => (
      <Box key={label} borderWidth="1px" borderRadius="md" px={3} py={2} minW="140px" flex="1">
        <Text fontSize="xs" color="gray.600">{label}</Text>
        <Text fontSize="2xl" fontWeight="700">{value}</Text>
      </Box>
    ))}
  </Flex>
);

const JobView = ({
  job, onRetry, onStartNew, onViewAudit, isRetrying,
}) => {
  const running = job.status === 'running';
  const counts = job.counts || {};
  const failures = job.failures || [];
  return (
    <>
      {running && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            This assignment is running. You can leave this page or start
            another assignment. It keeps going either way.
          </AlertDescription>
        </Alert>
      )}
      {countCards(running
        ? [['Processed', counts.processed || 0], ['Total', counts.total || 0]]
        : [
          ['Added', counts.added || 0],
          ['Role updated', counts.role_updated || 0],
          ['Skipped', counts.skipped || 0],
          ['Failed', counts.failed || 0],
        ])}
      {!running && failures.length > 0 && (
        <Box overflowX="auto" borderWidth="1px" borderRadius="md">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>User</Th>
                <Th>Tenant</Th>
                <Th>App</Th>
                <Th>Role</Th>
                <Th>Reason</Th>
              </Tr>
            </Thead>
            <Tbody>
              {failures.map((row) => (
                <Tr key={`${row.user.id}-${row.tenant.id}-${row.app}`}>
                  <Td>{`${row.user.first_name} ${row.user.last_name}`}</Td>
                  <Td>{row.tenant.name}</Td>
                  <Td>{row.app}</Td>
                  <Td>{row.role}</Td>
                  <Td>{row.reason}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      <Flex gap={3} wrap="wrap">
        <Button
          colorScheme="blue"
          color={NAVY}
          {...navyPress}
          onClick={onRetry}
          isDisabled={running || !failures.length}
          isLoading={isRetrying}
        >
          Retry failed
        </Button>
        <Button variant="outline" colorScheme="blue" onClick={onStartNew}>Start new assignment</Button>
        {!running && (
          <Button variant="outline" colorScheme="blue" onClick={onViewAudit}>View in audit log</Button>
        )}
      </Flex>
    </>
  );
};

const ReviewTab = ({
  preview, isFetching, isError, error, onBack, onRun, isStarting,
  job, onRetry, onStartNew, onViewAudit, isRetrying,
}) => {
  const totals = preview?.totals;
  const runnable = totals ? totals.new_assignments + totals.role_updates : 0;
  const inactiveCount = preview?.inactive_tenant_count || 0;
  const rows = preview?.rows || [];
  const userIds = [];
  rows.forEach((row) => {
    if (!userIds.includes(row.user.id)) {
      userIds.push(row.user.id);
    }
  });

  let body = null;
  if (isFetching) {
    body = <StatusAlert status="loading" alert_title="Building the review" />;
  } else if (isError) {
    body = <StatusAlert status="error" alert_title="Could not build the review" error={error} />;
  } else if (preview) {
    body = (
      <>
        {inactiveCount > 0 && (
          <Alert status="warning" borderRadius="md" alignItems="flex-start">
            <AlertIcon />
            <Box>
              <Text fontWeight="700">
                {`${countWord(inactiveCount, 'inactive tenant')} selected`}
              </Text>
              <Text>
                {`Users will still be assigned to ${
                  inactiveCount === 1 ? 'it' : 'them'
                }. Make sure this is intended before you run it.`}
              </Text>
            </Box>
          </Alert>
        )}
        {countCards([
          ['Total pairings', totals.pairings],
          ['New assignments', totals.new_assignments],
          ['Role updates', totals.role_updates],
          ['Already set, skipped', totals.unchanged],
        ])}
        <Box overflowX="auto" borderWidth="1px" borderRadius="md">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Tenant</Th>
                <Th>App</Th>
                <Th>Current role</Th>
                <Th>After this run</Th>
                <Th>Change</Th>
              </Tr>
            </Thead>
            <Tbody>
              {userIds.map((userId) => {
                const userRows = rows.filter((row) => row.user.id === userId);
                const { user } = userRows[0];
                return (
                  <React.Fragment key={userId}>
                    <Tr bg="gray.100">
                      <Td colSpan={5} fontWeight="700" color="blue.900">
                        {`${user.first_name} ${user.last_name}`}
                        <Text as="span" fontWeight="400" color="gray.600">{` · ${user.email}`}</Text>
                      </Td>
                    </Tr>
                    {userRows.map((row) => (
                      <Tr key={`${userId}-${row.tenant.id}-${row.app}`}>
                        <Td>
                          {row.tenant.name}
                          {row.tenant.inactive && <Badge ml={2} colorScheme="yellow">Inactive tenant</Badge>}
                          <Text fontFamily="mono" fontSize="xs" color="gray.600">{shortTenantId(row.tenant.id)}</Text>
                        </Td>
                        <Td>{row.app}</Td>
                        <Td>{row.current_role || <Text as="span" color="gray.500">Not assigned</Text>}</Td>
                        <Td>{row.next_role}</Td>
                        <Td>{changeBadge(row.change_type)}</Td>
                      </Tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </>
    );
  }

  const hint = job
    ? ''
    : (!totals
      ? ''
      : (runnable
        ? `Runs ${countWord(runnable, 'change')}. Takes a few minutes; you can leave the page.`
        : 'Nothing to change. Everyone already has these roles.'));

  return (
    <Panel
      title={job ? 'Assignment' : 'Review changes'}
      subtitle={job
        ? 'Progress stays here if you leave and come back.'
        : 'Check who is being added and whose role is changing before you run it.'}
      footer={job ? null : (
        <Flex px={5} py={3} gap={3} align="center" wrap="wrap" borderTopWidth="1px" bg="gray.50" borderBottomRadius="md">
          <Button variant="outline" colorScheme="blue" onClick={onBack}>Back</Button>
          <Box flex="1" />
          {hint && <Text fontSize="xs" color="gray.600">{hint}</Text>}
          <Button
            colorScheme="blue"
            color={NAVY}
            {...navyPress}
            onClick={onRun}
            isDisabled={!runnable || isStarting}
            isLoading={isStarting}
          >
            Run assignment
          </Button>
        </Flex>
      )}
    >
      {job ? (
        <JobView
          job={job}
          onRetry={onRetry}
          onStartNew={onStartNew}
          onViewAudit={onViewAudit}
          isRetrying={isRetrying}
        />
      ) : body}
    </Panel>
  );
};

export default ReviewTab;
